import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function toImageSrc(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${API}/files/${pathOrUrl}`;
}

// Default profile icon used everywhere an agent hasn't uploaded one.
// Served from /public so we don't depend on third-party hotlink protection
// (pngtree blocks hotlinking from foreign domains with HTTP 403).
export const DEFAULT_AVATAR_URL = "/default-avatar.png";
export function avatarSrc(pathOrUrl) {
  if (!pathOrUrl) return DEFAULT_AVATAR_URL;
  return toImageSrc(pathOrUrl);
}

/**
 * Client-side image compression.
 * - Skips non-image files and small files (< 500 KB).
 * - Downscales images whose longest side exceeds maxSide (default 1600 px).
 * - Re-encodes as JPEG at the given quality.
 * Returns a File (possibly the original) ready for upload.
 */
export async function compressImage(file, maxSide = 1600, quality = 0.85) {
  if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type)) return file;
  if (file.size < 500 * 1024) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("decode failed"));
      i.src = url;
    });
    const longSide = Math.max(img.width, img.height);
    if (longSide <= maxSide) return file;
    const scale = maxSide / longSide;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    const newName = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// --- Global 401 interceptor ------------------------------------------------
// When the auth cookie expires, every concurrent request returns 401 and the
// app used to spam "Not authenticated" toasts for every action. We now:
//   1. Show ONE consolidated "Session expired" toast.
//   2. Silence any further toast.error calls until the page reloads (so the
//      in-flight catch handlers in each page don't pile on extra toasts).
//   3. Redirect to /login (replace, so back-button doesn't bounce back).
// Paths that legitimately expect a 401 (auth check, login, register) are
// excluded so first-time visitors and bad-credential flows still work.
const AUTH_SKIP = ["/auth/me", "/auth/login", "/auth/register", "/auth/forgot-password", "/auth/reset-password"];
let __authExpiredHandled = false;

// Monkey-patch sonner's toast.error so it becomes a no-op once auth has
// expired. sonner exports `toast` as a singleton object — mutating its
// `.error` method here propagates to every caller.
const __origToastError = toast.error.bind(toast);
toast.error = (...args) => {
  if (__authExpiredHandled) return;
  return __origToastError(...args);
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = (error?.config?.url || "").toString();
    const isAuthRoute = AUTH_SKIP.some((p) => url.includes(p));
    // If we're already on a public route (e.g. /p/<id> from a share link),
    // don't bounce the user to /login when a background request 401s.
    const onPublicPath = typeof window !== "undefined" && window.location.pathname.startsWith("/p/");
    if (status === 401 && !isAuthRoute && !onPublicPath && !__authExpiredHandled) {
      __authExpiredHandled = true;
      try { toast.dismiss(); } catch (e) { /* ignore */ }
      __origToastError("Your session expired. Please sign in again.");
      // Give the toast ~300ms to render, then full reload to /login (clears
      // every React state + websocket + in-flight request).
      setTimeout(() => {
        if (typeof window !== "undefined") {
          window.location.replace("/login?expired=1");
        }
      }, 300);
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatCurrency(n) {
  if (n == null || n === "" || isNaN(Number(n))) return "—";
  const num = Number(n);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num.toLocaleString("en-IN")}`;
}
